import { Permission, Role } from '../../types/auth';

export class RoleService {
  private static instance: RoleService;
  private permissions: Map<string, Permission> = new Map();
  private roles: Map<string, Role> = new Map();

  private constructor() {
    this.initializePermissions();
    this.initializeRoles();
  }

  public static getInstance(): RoleService {
    if (!RoleService.instance) {
      RoleService.instance = new RoleService();
    }
    return RoleService.instance;
  }

  // Permission Management
  private initializePermissions(): void {
    const permissions: Permission[] = [
      // Lessons
      { id: 'lessons:read', name: 'Xem bài học', description: 'Có thể xem nội dung bài học', resource: 'lessons', action: 'read' },
      { id: 'lessons:write', name: 'Tạo/sửa bài học', description: 'Có thể tạo và chỉnh sửa bài học', resource: 'lessons', action: 'write' },
      { id: 'lessons:delete', name: 'Xóa bài học', description: 'Có thể xóa bài học', resource: 'lessons', action: 'delete' },
      
      // Quizzes
      { id: 'quizzes:read', name: 'Xem câu hỏi', description: 'Có thể xem câu hỏi trắc nghiệm', resource: 'quizzes', action: 'read' },
      { id: 'quizzes:write', name: 'Tạo/sửa câu hỏi', description: 'Có thể tạo và chỉnh sửa câu hỏi', resource: 'quizzes', action: 'write' },
      { id: 'quizzes:delete', name: 'Xóa câu hỏi', description: 'Có thể xóa câu hỏi', resource: 'quizzes', action: 'delete' },
      { id: 'quizzes:submit', name: 'Nộp bài trắc nghiệm', description: 'Có thể nộp bài trắc nghiệm', resource: 'quizzes', action: 'submit' },
      
      // Users
      { id: 'users:read', name: 'Xem người dùng', description: 'Có thể xem danh sách người dùng', resource: 'users', action: 'read' },
      { id: 'users:write', name: 'Tạo/sửa người dùng', description: 'Có thể tạo và chỉnh sửa người dùng', resource: 'users', action: 'write' },
      { id: 'users:delete', name: 'Xóa người dùng', description: 'Có thể xóa người dùng', resource: 'users', action: 'delete' },
      { id: 'users:manage', name: 'Quản lý người dùng', description: 'Có thể quản lý toàn bộ người dùng', resource: 'users', action: 'manage' },
      
      // Students
      { id: 'students:read', name: 'Xem học sinh', description: 'Có thể xem thông tin học sinh', resource: 'students', action: 'read' },
      { id: 'students:manage', name: 'Quản lý học sinh', description: 'Có thể quản lý học sinh', resource: 'students', action: 'manage' },
      
      // Teachers
      { id: 'teachers:approve', name: 'Duyệt giáo viên', description: 'Có thể duyệt tài khoản giáo viên', resource: 'teachers', action: 'approve' },
      
      // Profile
      { id: 'profile:read', name: 'Xem hồ sơ', description: 'Có thể xem hồ sơ cá nhân', resource: 'profile', action: 'read' },
      { id: 'profile:write', name: 'Sửa hồ sơ', description: 'Có thể chỉnh sửa hồ sơ cá nhân', resource: 'profile', action: 'write' },
      
      // Progress
      { id: 'progress:read', name: 'Xem tiến độ', description: 'Có thể xem tiến độ học tập', resource: 'progress', action: 'read' },
      { id: 'progress:manage', name: 'Quản lý tiến độ', description: 'Có thể quản lý tiến độ học tập', resource: 'progress', action: 'manage' },
      
      // Reports
      { id: 'reports:read', name: 'Xem báo cáo', description: 'Có thể xem báo cáo', resource: 'reports', action: 'read' },
      { id: 'reports:write', name: 'Tạo báo cáo', description: 'Có thể tạo báo cáo', resource: 'reports', action: 'write' },
      
      // System
      { id: 'system:manage', name: 'Quản lý hệ thống', description: 'Có thể quản lý toàn bộ hệ thống', resource: 'system', action: 'manage' },
    ];

    permissions.forEach(permission => {
      this.permissions.set(permission.id, permission);
    });
  }

  private initializeRoles(): void {
    const roles: Role[] = [
      {
        id: 'student',
        name: 'Học sinh',
        description: 'Người dùng học tập, có thể xem bài học và làm bài tập',
        permissions: [
          'lessons:read',
          'quizzes:read',
          'quizzes:submit',
          'profile:read',
          'profile:write',
          'progress:read',
        ],
        isSystemRole: true,
      },
      {
        id: 'teacher',
        name: 'Giáo viên',
        description: 'Giáo viên có thể tạo nội dung và quản lý học sinh',
        permissions: [
          'lessons:read',
          'lessons:write',
          'lessons:delete',
          'quizzes:read',
          'quizzes:write',
          'quizzes:delete',
          'students:read',
          'students:manage',
          'profile:read',
          'profile:write',
          'progress:read',
          'progress:manage',
          'reports:read',
          'reports:write',
        ],
        isSystemRole: true,
      },
      {
        id: 'admin',
        name: 'Quản trị viên',
        description: 'Quản trị viên có toàn quyền trong hệ thống',
        permissions: [
          'lessons:read',
          'lessons:write',
          'lessons:delete',
          'quizzes:read',
          'quizzes:write',
          'quizzes:delete',
          'users:read',
          'users:write',
          'users:delete',
          'users:manage',
          'teachers:approve',
          'students:read',
          'students:manage',
          'profile:read',
          'profile:write',
          'progress:read',
          'progress:manage',
          'reports:read',
          'reports:write',
          'system:manage',
        ],
        isSystemRole: true,
      },
    ];

    roles.forEach(role => {
      this.roles.set(role.id, role);
    });
  }

  // Public Methods
  getPermission(permissionId: string): Permission | undefined {
    return this.permissions.get(permissionId);
  }

  getRole(roleId: string): Role | undefined {
    return this.roles.get(roleId);
  }

  getAllPermissions(): Permission[] {
    return Array.from(this.permissions.values());
  }

  getAllRoles(): Role[] {
    return Array.from(this.roles.values());
  }

  getRolePermissions(roleId: string): string[] {
    const role = this.getRole(roleId);
    return role ? role.permissions : [];
  }

  hasPermission(userPermissions: string[], permissionId: string): boolean {
    return userPermissions.includes(permissionId);
  }

  hasAnyPermission(userPermissions: string[], permissionIds: string[]): boolean {
    return permissionIds.some(permissionId => userPermissions.includes(permissionId));
  }

  hasAllPermissions(userPermissions: string[], permissionIds: string[]): boolean {
    return permissionIds.every(permissionId => userPermissions.includes(permissionId));
  }

  canAccessResource(userPermissions: string[], resource: string, action: string): boolean {
    const permissionId = `${resource}:${action}`;
    return this.hasPermission(userPermissions, permissionId);
  }

  // Role-based access control helpers
  isStudent(userRole: string): boolean {
    return userRole === 'student';
  }

  isTeacher(userRole: string): boolean {
    return userRole === 'teacher';
  }

  isAdmin(userRole: string): boolean {
    return userRole === 'admin';
  }

  isTeacherOrAdmin(userRole: string): boolean {
    return this.isTeacher(userRole) || this.isAdmin(userRole);
  }

  // Permission checking for UI components
  canViewLessons(userPermissions: string[]): boolean {
    return this.hasPermission(userPermissions, 'lessons:read');
  }

  canCreateLessons(userPermissions: string[]): boolean {
    return this.hasPermission(userPermissions, 'lessons:write');
  }

  canManageStudents(userPermissions: string[]): boolean {
    return this.hasPermission(userPermissions, 'students:manage');
  }

  canApproveTeachers(userPermissions: string[]): boolean {
    return this.hasPermission(userPermissions, 'teachers:approve');
  }

  canManageSystem(userPermissions: string[]): boolean {
    return this.hasPermission(userPermissions, 'system:manage');
  }
}

export const roleService = RoleService.getInstance();
